import { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { Color, Flex } from "src/elements";
import { colorToRGBA } from "src/utils/colorToRgba";
import styled from "styled-components";
import { Scaleable } from "../../store/types";
import { getSplitShifts } from "../KonvaPlanogram/sceneProjection";

export const DEFAULT_NOTCH_RULER_WIDTH = 150;

export const getBayPdfShift = (bayIndex: number, rulerWidth: number): number =>
  (2 * bayIndex + 1) * rulerWidth;

export const getPdfPlanogramWidth = (
  planogramWidth: number,
  bayCount: number,
  rulerWidth: number,
): number => planogramWidth + bayCount * 2 * rulerWidth;

const PdfNotchRulerLayer = styled(Flex)`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 3;
`;

const PdfNotchRuler = styled.div`
  position: absolute;
  bottom: 0;
  background: linear-gradient(180deg, ${Color.yellow} 0%, ${Color.yellowActive} 100%);
  box-shadow: inset 0 0 0 1px ${colorToRGBA(Color.black, 0.95)};
`;

const PdfNotchTick = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background-color: ${Color.black};
  opacity: 0.95;
`;

export interface PdfNotchRulersProps extends Scaleable {
  bays: PlanogramBay[];
  rulerWidth?: number;
}

export const PdfNotchRulers = ({
  bays,
  scaleX,
  scaleY,
  rulerWidth = DEFAULT_NOTCH_RULER_WIDTH,
}: PdfNotchRulersProps) => {
  const splitShifts = getSplitShifts(bays);
  return (
    <PdfNotchRulerLayer aria-hidden="true">
      {bays.flatMap((bay, bayIndex) => {
        const scaledRulerWidth = rulerWidth * scaleX;
        const bayHeight = Math.max(bay.height, bay.notchOffset + bay.maxNotch * bay.notchSpacing);
        const tickPositions = Array.from(
          { length: bay.maxNotch },
          (_, notchIndex) => (bay.notchOffset + notchIndex * bay.notchSpacing) * scaleY,
        );

        const bayShift = getBayPdfShift(bayIndex, rulerWidth) + splitShifts[bayIndex];
        const leftRulerX = (bay.getXLeft() + bayShift - rulerWidth) * scaleX;
        const rightRulerX = (bay.getXRight() + bayShift) * scaleX;

        return [
          <PdfNotchRuler
            key={`${bay.uniqueId}-left`}
            data-testid="pdf-notch-ruler"
            style={{
              left: `${leftRulerX}px`,
              width: `${scaledRulerWidth}px`,
              height: `${bayHeight * scaleY}px`,
            }}
          >
            {tickPositions.map((bottom, notchIndex) => (
              <PdfNotchTick
                key={`${bay.uniqueId}-left-${notchIndex}`}
                style={{ bottom: `${bottom}px` }}
              />
            ))}
          </PdfNotchRuler>,
          <PdfNotchRuler
            key={`${bay.uniqueId}-right`}
            data-testid="pdf-notch-ruler"
            style={{
              left: `${rightRulerX}px`,
              width: `${scaledRulerWidth}px`,
              height: `${bayHeight * scaleY}px`,
            }}
          >
            {tickPositions.map((bottom, notchIndex) => (
              <PdfNotchTick
                key={`${bay.uniqueId}-right-${notchIndex}`}
                style={{ bottom: `${bottom}px` }}
              />
            ))}
          </PdfNotchRuler>,
        ];
      })}
    </PdfNotchRulerLayer>
  );
};
